<template>
  <div class="user-manage">
    <div class="toolbar">
      <el-button v-if="canCreate" type="primary" icon="el-icon-plus" size="small" @click="openAddDialog">新增用户</el-button>
      <el-button icon="el-icon-refresh" size="small" :loading="loading" @click="fetchData">刷新</el-button>
    </div>

    <el-table :data="users" size="small" v-loading="loading" border>
      <el-table-column prop="username" label="用户名" min-width="150"></el-table-column>
      <el-table-column label="状态" width="100" align="center">
        <template slot-scope="scope">
          <el-tag :type="scope.row.status === 'disabled' ? 'danger' : 'success'" size="mini">
            {{ scope.row.status === 'disabled' ? '禁用' : '启用' }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column label="角色" min-width="220">
        <template slot-scope="scope">
          <el-tag
            v-for="role in scope.row.roles"
            :key="role._id"
            size="mini"
            effect="plain"
            class="role-tag"
          >{{ role.name }}</el-tag>
          <span v-if="!scope.row.roles || scope.row.roles.length === 0" class="empty-text">未分配</span>
        </template>
      </el-table-column>
      <el-table-column label="数据范围" width="130" align="center">
        <template slot-scope="scope">
          {{ scope.row.dataScope === 'assigned_events' ? '指定赛事' : '全部数据' }}
        </template>
      </el-table-column>
      <el-table-column label="授权赛事数" width="110" align="center">
        <template slot-scope="scope">{{ (scope.row.eventIds || []).length }}</template>
      </el-table-column>
      <el-table-column label="品牌关键词" min-width="160" show-overflow-tooltip>
        <template slot-scope="scope">{{ (scope.row.brandKeywords || []).join('、') }}</template>
      </el-table-column>
      <el-table-column label="操作" :width="isMobile ? 74 : 290" fixed="right">
        <template slot-scope="scope">
          <div class="table-actions">
            <el-button v-if="canUpdate" type="text" size="mini" @click="openEditDialog(scope.row)">编辑</el-button>
            <el-button v-if="canUpdate" type="text" size="mini" @click="openPasswordDialog(scope.row)">重置密码</el-button>
            <el-button v-if="canViewLogs" type="text" size="mini" @click="viewUserLogs(scope.row)">日志</el-button>
            <el-button
              v-if="canDelete"
              type="text"
              size="mini"
              class="danger-action"
              @click="deleteUser(scope.row)"
            >删除</el-button>
          </div>
        </template>
      </el-table-column>
    </el-table>

    <el-dialog
      :title="dialogMode === 'add' ? '新增用户' : '编辑用户'"
      :visible.sync="dialogVisible"
      width="720px"
      :close-on-click-modal="false"
    >
      <el-form ref="form" :model="form" label-width="100px" size="small">
        <el-form-item label="用户名" required>
          <el-input
            v-model.trim="form.username"
            name="bde-admin-new-user-username"
            autocomplete="off"
            maxlength="100"
            placeholder="请输入用户名"
            :disabled="dialogMode === 'edit'"
          ></el-input>
        </el-form-item>
        <el-form-item v-if="dialogMode === 'add'" label="初始密码" required>
          <el-input
            v-model="form.password"
            name="bde-admin-new-user-password"
            autocomplete="new-password"
            show-password
            maxlength="100"
            placeholder="至少6位"
          ></el-input>
        </el-form-item>
        <el-form-item label="账号状态">
          <el-switch
            v-model="form.status"
            active-value="active"
            inactive-value="disabled"
            active-text="启用"
            inactive-text="禁用"
          ></el-switch>
        </el-form-item>
        <el-form-item label="角色">
          <el-checkbox-group v-model="form.roleIds">
            <el-checkbox
              v-for="role in roles"
              :key="role._id"
              :label="role._id"
            >
              {{ role.name }}
              <span class="role-code">{{ role.code }}</span>
            </el-checkbox>
          </el-checkbox-group>
        </el-form-item>
        <el-form-item label="数据范围">
          <el-radio-group v-model="form.dataScope">
            <el-radio label="all">全部数据</el-radio>
            <el-radio label="assigned_events">指定赛事</el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item label="赛事关键词">
          <el-input
            v-model="form.eventMatchKeywordsText"
            type="textarea"
            :rows="2"
            placeholder="用于匹配授权赛事，一行一个关键词，或用英文逗号分隔"
          ></el-input>
          <div class="field-actions">
            <el-button
              size="mini"
              icon="el-icon-search"
              :loading="matchingEvents"
              :disabled="!canUpdate"
              @click="matchEventsByKeywords(true)"
            >按关键词匹配赛事</el-button>
            <span class="field-tip">匹配结果分页展示，勾选后追加到下方授权赛事列表</span>
          </div>
        </el-form-item>
        <el-form-item label="赛事ID">
          <el-table
            ref="authorizedEventTable"
            :data="authorizedEvents"
            :row-key="getEventRowKey"
            size="mini"
            border
            max-height="260"
            empty-text="暂无授权赛事"
            @selection-change="handleAuthorizedEventSelection"
          >
            <el-table-column type="selection" width="45"></el-table-column>
            <el-table-column prop="_id" label="赛事ID" width="170">
              <template slot-scope="scope">
                <copy-text :value="scope.row._id"></copy-text>
              </template>
            </el-table-column>
            <el-table-column prop="name" label="赛事名称" min-width="220" show-overflow-tooltip></el-table-column>
            <el-table-column label="赛事时间" width="120">
              <template slot-scope="scope">{{ formatEventDate(scope.row) }}</template>
            </el-table-column>
            <el-table-column prop="address" label="赛事地点" min-width="180" show-overflow-tooltip></el-table-column>
          </el-table>
          <div class="field-tip event-list-tip">取消勾选并保存后，会从该用户授权赛事中移除。</div>
        </el-form-item>
        <el-form-item label="品牌关键词">
          <el-input
            v-model="form.brandKeywordsText"
            type="textarea"
            :rows="3"
            placeholder="一行一个关键词，或用英文逗号分隔"
          ></el-input>
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button size="small" @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" size="small" :loading="saving" @click="saveUser">保存</el-button>
      </span>
    </el-dialog>

    <el-dialog
      title="重置密码"
      :visible.sync="passwordDialogVisible"
      width="420px"
      :close-on-click-modal="false"
    >
      <el-form label-width="80px" size="small">
        <el-form-item label="用户">
          <span>{{ passwordForm.username }}</span>
        </el-form-item>
        <el-form-item label="新密码" required>
          <el-input
            v-model="passwordForm.password"
            name="bde-admin-reset-user-password"
            autocomplete="new-password"
            show-password
            maxlength="100"
            placeholder="至少6位"
          ></el-input>
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button size="small" @click="passwordDialogVisible = false">取消</el-button>
        <el-button type="primary" size="small" :loading="savingPassword" @click="resetPassword">保存</el-button>
      </span>
    </el-dialog>

    <el-dialog
      title="匹配赛事"
      :visible.sync="eventDialogVisible"
      width="860px"
      :close-on-click-modal="false"
    >
      <div class="match-summary">
        <span>关键词：{{ matchedKeywords.join('、') || '--' }}</span>
        <span>已选 {{ selectedMatchedEvents.length }} 场，共 {{ matchedEventTotal }} 场匹配</span>
      </div>
      <el-table
        ref="matchedEventTable"
        :data="matchedEvents"
        size="small"
        border
        max-height="420"
        @selection-change="handleMatchedEventSelection"
      >
        <el-table-column type="selection" width="45"></el-table-column>
        <el-table-column prop="_id" label="赛事ID" width="170">
          <template slot-scope="scope">
            <copy-text :value="scope.row._id"></copy-text>
          </template>
        </el-table-column>
        <el-table-column prop="name" label="赛事名称" min-width="240" show-overflow-tooltip></el-table-column>
        <el-table-column label="赛事时间" width="120">
          <template slot-scope="scope">{{ formatEventDate(scope.row) }}</template>
        </el-table-column>
        <el-table-column prop="address" label="赛事地点" min-width="180" show-overflow-tooltip></el-table-column>
        <el-table-column label="命中关键词" min-width="140" show-overflow-tooltip>
          <template slot-scope="scope">{{ (scope.row.matchedKeywords || []).join('、') }}</template>
        </el-table-column>
      </el-table>
      <el-pagination
        v-if="matchedEventTotal > matchedEventPageSize"
        class="match-pager"
        small
        background
        layout="prev, pager, next"
        :total="matchedEventTotal"
        :page-size="matchedEventPageSize"
        :current-page.sync="matchedEventPageNum"
        @current-change="handleMatchedEventPageChange"
      ></el-pagination>
      <span slot="footer">
        <el-button size="small" @click="eventDialogVisible = false">取消</el-button>
        <el-button size="small" @click="selectAllMatchedEvents">全选当前页</el-button>
        <el-button type="primary" size="small" @click="appendSelectedEvents">追加到授权赛事</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { getToken, hasPermission } from '@/utils/auth.js';

const emptyForm = () => ({
  id: '',
  username: '',
  password: '',
  status: 'active',
  roleIds: [],
  dataScope: 'all',
  eventIds: [],
  eventMatchKeywordsText: '',
  brandKeywordsText: ''
});

const splitTextList = (text) => {
  return String(text || '')
    .split(/[\n,]/)
    .map(item => item.trim())
    .filter(Boolean);
};

const joinTextList = (list) => {
  return Array.isArray(list) ? list.join('\n') : '';
};

export default {
  name: 'UserManage',
  data() {
    return {
      loading: false,
      saving: false,
      savingPassword: false,
      users: [],
      roles: [],
      dialogVisible: false,
      passwordDialogVisible: false,
      eventDialogVisible: false,
      dialogMode: 'add',
      form: emptyForm(),
      passwordForm: {
        id: '',
        username: '',
        password: ''
      },
      matchingEvents: false,
      matchedKeywords: [],
      matchedEvents: [],
      matchedEventTotal: 0,
      matchedEventPageNum: 1,
      matchedEventPageSize: 10,
      selectedMatchedEventMap: {},
      authorizedEvents: [],
      syncingAuthorizedSelection: false,
      isMobile: false
    };
  },
  created() {
    this.fetchData();
  },
  mounted() {
    this.updateViewport();
    window.addEventListener('resize', this.updateViewport);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.updateViewport);
  },
  computed: {
    canCreate() {
      return hasPermission('system:user:create');
    },
    canUpdate() {
      return hasPermission('system:user:update');
    },
    canDelete() {
      return hasPermission('system:user:delete');
    },
    canViewLogs() {
      return hasPermission('*');
    },
    selectedMatchedEvents() {
      return Object.keys(this.selectedMatchedEventMap).map(id => this.selectedMatchedEventMap[id]);
    }
  },
  methods: {
    authHeaders() {
      return {
        'x-auth-token': getToken()
      };
    },
    updateViewport() {
      this.isMobile = window.innerWidth <= 768;
    },
    fetchData() {
      this.loading = true;
      Promise.all([
        this.$axios.get('/admin-users', { headers: this.authHeaders() }),
        this.$axios.get('/roles', { headers: this.authHeaders() })
      ]).then(([usersRes, rolesRes]) => {
        this.users = usersRes.data.res || [];
        this.roles = rolesRes.data.res || [];
      }).catch((err) => {
        this.$message.error(err.response?.data?.message || '用户数据加载失败');
      }).finally(() => {
        this.loading = false;
      });
    },
    openAddDialog() {
      this.dialogMode = 'add';
      this.form = emptyForm();
      this.authorizedEvents = [];
      this.selectedMatchedEventMap = {};
      this.dialogVisible = true;
      this.$nextTick(() => {
        this.form.username = '';
        this.form.password = '';
      });
    },
    openEditDialog(user) {
      this.dialogMode = 'edit';
      this.form = {
        id: user._id,
        username: user.username,
        password: '',
        status: user.status || 'active',
        roleIds: [...(user.roleIds || [])],
        dataScope: user.dataScope || 'all',
        eventIds: [...(user.eventIds || [])],
        eventMatchKeywordsText: '',
        brandKeywordsText: joinTextList(user.brandKeywords)
      };
      this.authorizedEvents = [];
      this.selectedMatchedEventMap = {};
      this.dialogVisible = true;
      this.loadAuthorizedEvents(this.form.eventIds);
    },
    buildPayload() {
      return {
        id: this.form.id,
        username: this.form.username,
        password: this.form.password,
        status: this.form.status,
        roleIds: this.form.roleIds,
        dataScope: this.form.dataScope,
        eventIds: this.form.eventIds,
        brandKeywords: splitTextList(this.form.brandKeywordsText)
      };
    },
    saveUser() {
      if (!this.form.username) {
        this.$message.error('用户名不能为空');
        return;
      }
      if (this.dialogMode === 'add' && (!this.form.password || this.form.password.length < 6)) {
        this.$message.error('初始密码不能少于6位');
        return;
      }
      this.saving = true;
      const url = this.dialogMode === 'add' ? '/admin-users/add' : '/admin-users/update';
      this.$axios.post(url, JSON.stringify(this.buildPayload()), {
        headers: this.authHeaders()
      }).then(() => {
        this.$message.success('保存成功');
        this.dialogVisible = false;
        this.fetchData();
      }).catch((err) => {
        this.$message.error(err.response?.data?.message || '保存失败');
      }).finally(() => {
        this.saving = false;
      });
    },
    openPasswordDialog(user) {
      this.passwordForm = {
        id: user._id,
        username: user.username,
        password: ''
      };
      this.passwordDialogVisible = true;
      this.$nextTick(() => {
        this.passwordForm.password = '';
      });
    },
    resetPassword() {
      if (!this.passwordForm.password || this.passwordForm.password.length < 6) {
        this.$message.error('新密码不能少于6位');
        return;
      }
      this.savingPassword = true;
      this.$axios.post('/admin-users/reset-password', JSON.stringify({
        id: this.passwordForm.id,
        password: this.passwordForm.password
      }), {
        headers: this.authHeaders()
      }).then(() => {
        this.$message.success('密码已重置');
        this.passwordDialogVisible = false;
      }).catch((err) => {
        this.$message.error(err.response?.data?.message || '重置失败');
      }).finally(() => {
        this.savingPassword = false;
      });
    },
    deleteUser(user) {
      this.$confirm(`确认删除用户「${user.username}」？`, '删除确认', {
        type: 'warning'
      }).then(() => {
        return this.$axios.post('/admin-users/delete', JSON.stringify({ id: user._id }), {
          headers: this.authHeaders()
        });
      }).then(() => {
        this.$message.success('删除成功');
        this.fetchData();
      }).catch((err) => {
        if (err === 'cancel') return;
        this.$message.error(err.response?.data?.message || '删除失败');
      });
    },
    viewUserLogs(user) {
      if (!user || !user.username) return;
      this.$router.push({
        path: '/operation-logs',
        query: {
          username: user.username
        }
      });
    },
    loadAuthorizedEvents(eventIds = []) {
      const ids = Array.isArray(eventIds) ? eventIds.map(id => String(id)).filter(Boolean) : [];
      if (ids.length === 0) {
        this.syncingAuthorizedSelection = true;
        this.authorizedEvents = [];
        this.form.eventIds = [];
        this.syncAuthorizedSelection();
        return;
      }
      this.$axios.post('/admin-users/events-by-ids', JSON.stringify({ eventIds: ids }), {
        headers: this.authHeaders()
      }).then((res) => {
        this.syncingAuthorizedSelection = true;
        this.authorizedEvents = res.data.res || [];
        this.form.eventIds = ids;
        this.syncAuthorizedSelection();
      }).catch((err) => {
        this.$message.error(err.response?.data?.message || '授权赛事加载失败');
      });
    },
    syncAuthorizedSelection() {
      this.$nextTick(() => {
        const table = this.$refs.authorizedEventTable;
        if (!table) {
          this.syncingAuthorizedSelection = false;
          return;
        }
        this.syncingAuthorizedSelection = true;
        table.clearSelection();
        const selectedIds = this.form.eventIds.map(id => String(id));
        this.authorizedEvents.forEach((event) => {
          if (selectedIds.includes(String(event._id))) {
            table.toggleRowSelection(event, true);
          }
        });
        this.$nextTick(() => {
          this.syncingAuthorizedSelection = false;
        });
      });
    },
    handleAuthorizedEventSelection(selection) {
      if (this.syncingAuthorizedSelection) return;
      this.form.eventIds = selection.map(event => String(event._id));
    },
    matchEventsByKeywords(resetPage = true) {
      const keywords = splitTextList(this.form.eventMatchKeywordsText);
      if (keywords.length === 0) {
        this.$message.error('请先填写赛事关键词');
        return;
      }
      if (resetPage) {
        this.matchedEventPageNum = 1;
        this.selectedMatchedEventMap = {};
      }
      this.matchingEvents = true;
      this.$axios.post('/admin-users/match-events', JSON.stringify({
        keywords,
        pageNum: this.matchedEventPageNum,
        pageSize: this.matchedEventPageSize
      }), {
        headers: this.authHeaders()
      }).then((res) => {
        this.matchedKeywords = keywords;
        const result = res.data.res || {};
        this.matchedEvents = result.list || [];
        this.matchedEventTotal = result.total || 0;
        this.matchedEventPageNum = result.pageNum || this.matchedEventPageNum;
        this.matchedEventPageSize = result.pageSize || this.matchedEventPageSize;
        this.eventDialogVisible = true;
        this.syncMatchedSelection();
      }).catch((err) => {
        this.$message.error(err.response?.data?.message || '匹配赛事失败');
      }).finally(() => {
        this.matchingEvents = false;
      });
    },
    syncMatchedSelection() {
      this.$nextTick(() => {
        const table = this.$refs.matchedEventTable;
        if (!table) return;
        table.clearSelection();
        const currentIds = this.form.eventIds.map(id => String(id));
        this.matchedEvents.forEach((event) => {
          const id = String(event._id);
          if (currentIds.includes(id) || this.selectedMatchedEventMap[id]) {
            table.toggleRowSelection(event, true);
          }
        });
      });
    },
    handleMatchedEventSelection(selection) {
      const selectedIds = selection.map(event => String(event._id));
      this.matchedEvents.forEach((event) => {
        const id = String(event._id);
        if (selectedIds.includes(id)) {
          this.$set(this.selectedMatchedEventMap, id, event);
        } else {
          this.$delete(this.selectedMatchedEventMap, id);
        }
      });
    },
    handleMatchedEventPageChange(page) {
      this.matchedEventPageNum = page;
      this.matchEventsByKeywords(false);
    },
    selectAllMatchedEvents() {
      const table = this.$refs.matchedEventTable;
      if (!table) return;
      this.matchedEvents.forEach(event => table.toggleRowSelection(event, true));
    },
    appendSelectedEvents() {
      if (this.selectedMatchedEvents.length === 0) {
        this.$message.error('请至少选择一场赛事');
        return;
      }
      const currentIds = this.form.eventIds.map(id => String(id));
      const nextIds = Array.from(new Set(currentIds.concat(
        this.selectedMatchedEvents.map(event => String(event._id))
      )));
      const eventMap = this.authorizedEvents.reduce((map, event) => {
        map[String(event._id)] = event;
        return map;
      }, {});
      this.selectedMatchedEvents.forEach((event) => {
        eventMap[String(event._id)] = event;
      });
      this.syncingAuthorizedSelection = true;
      this.authorizedEvents = nextIds.map(id => eventMap[id]).filter(Boolean);
      this.form.eventIds = nextIds;
      this.eventDialogVisible = false;
      this.syncAuthorizedSelection();
      this.$message.success(`已追加 ${this.selectedMatchedEvents.length} 场赛事`);
    },
    formatEventDate(event) {
      if (event.fuzzyTime) return event.fuzzyTime;
      if (!event.startTime) return '--';
      return this.$dayjs(event.startTime).format('YYYY-MM-DD');
    },
    getEventRowKey(event) {
      return String(event && event._id);
    },
    formatOrganizer(event) {
      return [
        event.hosting,
        event.undertaking,
        event.host,
        event.undertake
      ].filter(Boolean).join(' / ') || '--';
    }
  }
};
</script>

<style scoped>
.user-manage {
  width: 100%;
}

.toolbar {
  display: flex;
  gap: 8px;
  margin-bottom: 12px;
}

.role-tag {
  margin-right: 6px;
}

.role-code {
  margin-left: 4px;
  color: var(--text-secondary);
  font-size: 12px;
}

.empty-text {
  color: var(--text-secondary);
}

.field-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
}

.field-tip {
  color: var(--text-secondary);
  font-size: 12px;
}

.event-list-tip {
  margin-top: 6px;
}

.match-summary {
  display: flex;
  justify-content: space-between;
  margin-bottom: 10px;
  color: var(--text-secondary);
  font-size: 13px;
}

.match-pager {
  margin-top: 12px;
  text-align: right;
}

.danger-action {
  color: #f56c6c;
}

.table-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.table-actions .el-button {
  margin: 0;
}

.user-manage /deep/ .el-checkbox {
  margin-right: 18px;
  margin-bottom: 8px;
}

@media (max-width: 768px) {
  .table-actions {
    flex-direction: column;
    align-items: center;
  }
}
</style>
